import {
  Alert,
  AlertProps,
  ComponentEventHandler,
  Flex,
  FocusTrapZone,
  Text
} from '@fluentui/react-northstar';
import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { bannerErrorStyles } from './BannerErrorContext.styles';
import {
  BannerProps,
  IBannerErrorContext,
  IBannerErrorContextDataProps
} from './BannerErrorContext.types';

const initialContext: IBannerErrorContext = {
  addBanner: () => undefined,
  removeBanner: () => undefined
};

const BannerErrorContext = createContext(initialContext);

export const BannerErrorContextProvider: React.FC<IBannerErrorContextDataProps> = ({
  children
}) => {
  const [banners, setBanners] = useState<Record<string, BannerProps>>({});
  const { t } = useTranslation();
  const addBanner = useCallback((props: BannerProps) => {
    setBanners(oldState => ({
      ...oldState,
      [props.alertId]: props
    }));
  }, []);

  const removeBanner = useCallback((alertId: string) => {
    setBanners(oldState => {
      delete oldState[alertId];
      return { ...oldState };
    });
  }, []);

  const removeBannerOnClick = useCallback(
    (alertId: string): ComponentEventHandler<AlertProps> => () => {
      removeBanner(alertId);
    },
    [removeBanner]
  );

  const value = useMemo(
    () => ({
      addBanner,
      removeBanner
    }),
    [addBanner, removeBanner]
  );

  const alerts = useMemo(() => {
    const bannerArray = Object.values(banners);
    if (bannerArray.length) {
      const result = bannerArray.map(({ alertId, content, actions }) => {
        const textContent = (
          <Flex
            fill
            hAlign="center"
            styles={actions ? bannerErrorStyles.centerWithActions : undefined}
          >
            <Text content={content} weight="semibold" />
          </Flex>
        );

        return (
          <FocusTrapZone key={alertId}>
            <Alert
              key={alertId}
              id={alertId}
              as="div"
              styles={bannerErrorStyles.bannerErrorAlertStyles}
              visible
              dismissible
              dismissAction={{
                'aria-label': t('Close Alert'),
                icon: 'close',
                iconOnly: true,
                onClick: removeBannerOnClick(alertId)
              }}
              danger
              attached="bottom"
              content={textContent}
              actions={actions}
            />
          </FocusTrapZone>
        );
      });
      return result;
    }
    return null;
  }, [banners, removeBannerOnClick, t]);

  return (
    <BannerErrorContext.Provider value={value}>
      {alerts}
      {children}
    </BannerErrorContext.Provider>
  );
};

export const useErrorBannerContext = () => useContext(BannerErrorContext);
